/**
 * Modal wrapper around OfferedAppointmentTypesEditor, launched from
 * appointment-type selects so providers can update which types they offer
 * without leaving the dialog they're in.
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../contexts/AuthContext';
import OfferedAppointmentTypesEditor from './OfferedAppointmentTypesEditor';

interface OfferedAppointmentTypesModalProps {
  open: boolean;
  onClose: () => void;
  /** Practitioner to edit; defaults to the logged-in user's practitioner. */
  practitionerId?: string | null;
  /** Display name of the practitioner being edited, when known. */
  providerName?: string | null;
  /** Called after a successful save (e.g. to refetch appointment types). */
  onSaved?: () => void;
}

export default function OfferedAppointmentTypesModal({
  open,
  onClose,
  practitionerId,
  providerName,
  onSaved,
}: OfferedAppointmentTypesModalProps) {
  const { user } = useAuth();
  const authUser = user && typeof user === 'object' ? user : null;
  const selfPractitionerId = (authUser as { practitioner_id?: string })?.practitioner_id;
  const isSelf = !practitionerId || practitionerId === selfPractitionerId;
  const title = isSelf
    ? 'Appointment Types I Offer'
    : providerName
      ? `Appointment Types ${providerName} Offers`
      : 'Offered Appointment Types';
  const ownerLabel = isSelf ? 'you' : providerName || 'this provider';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span">{title}</Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          Select which appointment types {ownerLabel} offer{isSelf ? '' : 's'}. This filters
          appointment types throughout the app.
          {isSelf && ' Choose Client Portal and/or Booking Widget for the self-scheduling channels you want to offer.'}
        </Typography>
        <OfferedAppointmentTypesEditor
          practitionerId={practitionerId}
          onSaved={() => {
            onSaved?.();
            onClose();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
